import { styled } from '@mui/material/styles';
import Section from './Section';
import { FormattedMessage } from 'react-intl';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioButton from '@/components/RadioButton/RadioButton';
import { useQuery } from '@tanstack/react-query';
import { fetchFilters } from '@/lib/api';
import { mapConfigToOptions } from '@/lib/listOptions';
import Markets from '@/constants/Markets';
import CircularProgress from '@mui/material/CircularProgress';
import FilterOptionsUnavailable from './FilterOptionsUnavailable';

const Market = ({
  value,
  onChange,
  mode = 'light',
  lessMarkets = false,
}: {
  value: number | null;
  onChange: (value: number | null) => void;
  mode?: 'light' | 'dark';
  lessMarkets?: boolean;
}) => {
  const { isPending, isError, data } = useQuery({
    queryKey: ['filters'],
    queryFn: fetchFilters,
  });

  const filteredMarkets = lessMarkets
    ? data?.markets?.filter(
        (market) =>
          market.market_name !== 'curacao' &&
          market.market_name !== 'south_africa',
      )
    : data?.markets;

  const marketItems = mapConfigToOptions(Markets, filteredMarkets, {
    id: 'market_id',
    name: 'market_name',
  });

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const marketId = (event.target as HTMLInputElement).value;
    onChange(marketId ? Number(marketId) : null);
  };

  if (isPending) {
    return (
      <Section
        title={
          <FormattedMessage id="market" defaultMessage="Market" />
        }
        aria-label="Market filter"
        mode={mode}
        defaultOpen={value !== null}
      >
        <LoaderWrapper role="status" aria-label="Loading markets">
          <CircularProgress />
        </LoaderWrapper>
      </Section>
    );
  }

  return (
    <Section
      title={<FormattedMessage id="market" defaultMessage="Market" />}
      aria-label="Market filter"
      mode={mode}
      defaultOpen={value !== null}
    >
      {isError && !data ? (
        <FilterOptionsUnavailable />
      ) : (
        <Content>
          <StyledFormControl>
            <RadioGroup
              value={value?.toString() || ''}
              name="market"
              onChange={handleChange}
            >
              {marketItems.map(({ id, value: marketValue, text }) => (
                <FormControlLabel
                  key={id}
                  value={marketValue.toString()}
                  control={<RadioButton mode={mode} />}
                  label={text}
                  aria-label={`Select ${text} market`}
                />
              ))}
            </RadioGroup>
          </StyledFormControl>
        </Content>
      )}
    </Section>
  );
};

const Content = styled('div')({
  display: 'block',
  height: '180px',
});

const StyledFormControl = styled(FormControl)({
  display: 'block',
  height: '180px',
  maxHeight: '180px',
  overflowY: 'auto',
});

const LoaderWrapper = styled('div')({
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '180px',
});

export default Market;
